'use client';

import React, { Suspense, lazy, useEffect, useContext } from 'react';
import { VibeshipContext } from '../contexts/VibeshipContext';
import { DependencyContext } from './VibeshipProviderWithDeps';
import { logger } from '../utils/logger';
import { useUIState } from '@vibeship/ui';
import { keyboardHandlerManager } from '../utils/KeyboardHandlerManager';

// Import and inject styles dynamically
const injectStyles = () => {
  if (typeof window === 'undefined') return;
  
  // Check if styles are already injected
  if (document.getElementById('vibeship-ui-styles')) return;
  
  // Try to import the styles module
  try {
    // @ts-ignore - Dynamic import of CSS
    import('@vibeship/ui/styles').then(() => {
      logger.log('[VibeshipDevTools] Styles imported successfully');
    }).catch(() => {
      // Fallback: inject styles via link tag
      const link = document.createElement('link');
      link.id = 'vibeship-ui-styles';
      link.rel = 'stylesheet';
      link.href = '/node_modules/@vibeship/ui/dist/styles.css';
      document.head.appendChild(link);
      logger.log('[VibeshipDevTools] Styles injected via link tag');
    });
  } catch (err) {
    logger.warn('[VibeshipDevTools] Failed to inject styles:', err);
  }
};

// Execute style injection and initialize logger
if (typeof window !== 'undefined') {
  injectStyles();
  logger.init();
}

// Lazy load TaskPanelOverlay from @vibeship/ui
const TaskPanelOverlay = lazy(() => {
  logger.log('[VibeshipDevTools] Loading TaskPanelOverlay from @vibeship/ui...');
  return import('@vibeship/ui').then(module => {
    logger.log('[VibeshipDevTools] Module loaded, exports:', Object.keys(module));
    if (!module.TaskPanelOverlay) {
      throw new Error('TaskPanelOverlay not found in @vibeship/ui');
    }
    logger.log('[VibeshipDevTools] TaskPanelOverlay found successfully');
    return { default: module.TaskPanelOverlay };
  }).catch(error => {
    logger.error('[VibeshipDevTools] Failed to load TaskPanelOverlay:', error);
    throw error;
  });
});

// Default implementations for dependencies
const createDefaultGetProjectTasks = (apiBasePath: string) => async (params: any) => {
  try {
    const endpoint = `${apiBasePath}/tasks`;
    const queryParams = new URLSearchParams({
      path: params.scanDirectory || '.',
      limit: '1000',
      type: 'all',
      status: 'all'
    });
    
    const response = await fetch(`${endpoint}?${queryParams}`);
    if (!response.ok) {
      logger.error('[VibeshipDevTools] Failed to fetch tasks:', response.status);
      return [];
    }
    
    const data = await response.json();
    return data.tasks || [];
  } catch (error) {
    logger.error('[VibeshipDevTools] Error fetching tasks:', error);
    return [];
  }
};

const createDefaultSSEClient = (endpoint: string) => {
  let eventSource: EventSource | null = null;
  
  return {
    connect: () => {
      if (!eventSource) {
        eventSource = new EventSource(endpoint);
        logger.log('[VibeshipDevTools] SSE connected');
      }
    },
    disconnect: () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    }
  };
};

// Component to handle keyboard shortcuts for TaskPanelOverlay
function KeyboardShortcutHandler() {
  useEffect(() => {
    logger.log('[VibeshipDevTools] Registering keyboard shortcut handler for TaskPanelOverlay (Cmd+Shift+K)');
    
    // Register with the keyboard handler manager
    const unregister = keyboardHandlerManager.register({
      id: 'vibeship-task-panel-toggle',
      keys: ['cmd', 'shift', 'k'],
      handler: (event: KeyboardEvent) => {
        logger.log('[VibeshipDevTools] Keyboard shortcut triggered: Cmd+Shift+K', {
          key: event.key,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
        });
        
        try {
          const state = useUIState.getState();
          const currentState = state.taskPanelOpen;
          logger.log('[VibeshipDevTools] Current taskPanelOpen state:', currentState);
          
          // Use the debounced toggle to prevent rapid toggling
          state.debouncedToggleTaskPanel();
          
          // Log the action (actual state change will be logged by the store)
          logger.log('[VibeshipDevTools] TaskPanel toggle requested');
        } catch (error) {
          logger.error('[VibeshipDevTools] Failed to toggle task panel:', error);
        }
      },
      options: {
        preventDefault: true,
        stopPropagation: true,
        capture: true,
        debounceMs: 0 // We're using the store's debouncing
      }
    });
    
    // Debug: Log current handlers
    const handlers = keyboardHandlerManager.getHandlerInfo();
    logger.log('[VibeshipDevTools] Registered keyboard handlers:', handlers);
    
    return () => {
      logger.log('[VibeshipDevTools] Unregistering keyboard shortcut handler');
      unregister();
    };
  }, []);

  return null;
}

function DevToolsContent() {
  const context = useContext(VibeshipContext);
  const dependencies = useContext(DependencyContext);
  const apiBasePath = context?.config?.api?.basePath || '/api/vibeship';

  logger.log('[VibeshipDevTools] DevToolsContent rendering');
  logger.log('[VibeshipDevTools] Context:', context);
  logger.log('[VibeshipDevTools] Dependencies:', dependencies);
  logger.log('[VibeshipDevTools] Dependencies keys:', Object.keys(dependencies));
  logger.log('[VibeshipDevTools] AISettingsPanel provided:', !!dependencies.AISettingsPanel);
  logger.log('[VibeshipDevTools] AISettingsPanel type:', typeof dependencies.AISettingsPanel);
  logger.log('[VibeshipDevTools] AISettingsPanel name:', dependencies.AISettingsPanel?.name || 'None');

  // Default implementations
  const defaultLoadFileContent = async (filePath: string) => {
    const response = await fetch(`${apiBasePath}/files/content?path=${encodeURIComponent(filePath)}`);
    if (!response.ok) throw new Error('Failed to load file');
    const data = await response.json();
    return data.content;
  };

  const defaultSaveFileContent = async (filePath: string, content: string) => {
    const response = await fetch(`${apiBasePath}/files/content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: filePath, content }),
    });
    if (!response.ok) throw new Error('Failed to save file');
  };

  const defaultOnLoadApiKey = async (provider: string) => {
    const key = localStorage.getItem(`vibeship-api-key-${provider}`);
    return key || '';
  };

  // Prepare all dependencies
  const taskPanelProps = {
    // Task Panel dependencies
    getProjectTasks: dependencies.getProjectTasks || createDefaultGetProjectTasks(apiBasePath),
    sseEndpoint: dependencies.sseEndpoint || `${apiBasePath}/tasks/stream`,
    getSSEClient: dependencies.getSSEClient || createDefaultSSEClient,
    
    // AI Chat dependencies
    onLoadApiKey: dependencies.onLoadApiKey || defaultOnLoadApiKey,
    chatApiEndpoint: dependencies.chatApiEndpoint || `${apiBasePath}/ai/chat`,
    
    // Settings Panel dependencies
    AISettingsPanel: dependencies.AISettingsPanel,
    
    // File Editor dependencies
    loadFileContent: dependencies.loadFileContent || defaultLoadFileContent,
    saveFileContent: dependencies.saveFileContent || defaultSaveFileContent,
  };

  logger.log('[VibeshipDevTools] TaskPanelOverlay props:', {
    hasGetProjectTasks: !!taskPanelProps.getProjectTasks,
    hasSSEEndpoint: !!taskPanelProps.sseEndpoint,
    hasGetSSEClient: !!taskPanelProps.getSSEClient,
    hasOnLoadApiKey: !!taskPanelProps.onLoadApiKey,
    hasChatApiEndpoint: !!taskPanelProps.chatApiEndpoint,
    hasAISettingsPanel: !!taskPanelProps.AISettingsPanel,
    AISettingsPanelType: typeof taskPanelProps.AISettingsPanel,
    AISettingsPanelName: taskPanelProps.AISettingsPanel?.name || 'None',
    hasLoadFileContent: !!taskPanelProps.loadFileContent,
    hasSaveFileContent: !!taskPanelProps.saveFileContent,
  });

  // DON'T pass isOpen or onToggle - let TaskPanelOverlay manage its own state via useUIState
  return (
    <>
      <KeyboardShortcutHandler />
      <TaskPanelOverlay {...taskPanelProps} />
    </>
  );
}

// Error boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    logger.error('[VibeshipDevTools] Error boundary caught:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('[VibeshipDevTools] Error details:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          right: '20px', 
          background: 'red', 
          color: 'white', 
          padding: '10px', 
          borderRadius: '5px',
          zIndex: 9999
        }}>
          DevTools Error: {this.state.error?.message || 'Unknown error'}
        </div>
      );
    }

    return this.props.children;
  }
}

export function VibeshipDevTools() {
  logger.log('[VibeshipDevTools] Main component rendering');
  
  return (
    <div data-vibeship-devtools="true" style={{ display: 'contents' }}>
      <ErrorBoundary>
        <Suspense fallback={
          <div style={{ 
            position: 'fixed', 
            bottom: '20px', 
            left: '20px', 
            background: '#3b82f6', 
            color: 'white', 
            padding: '10px 15px', 
            borderRadius: '5px',
            zIndex: 9999,
            fontSize: '14px'
          }}>
            Loading DevTools...
          </div>
        }>
          <DevToolsContent />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}